<template>
    <v-dialog
        :value="showModal"
        @input="onShowChange"
        max-width="720"
        scrollable
    >
        <v-snackbar
            v-model="feedback.show"
            :color="feedback.color"
            :timeout="feedback.color === 'error' ? 6000 : 3500"
            top right
        >
            <v-icon left dark>{{ feedback.color === 'error' ? 'mdi-alert-circle' : 'mdi-check-circle' }}</v-icon>
            {{ feedback.message }}
        </v-snackbar>
        <v-card>
            <v-card-title class="d-flex align-center">
                <v-icon left color="indigo darken-2">mdi-auto-fix</v-icon>
                Sugestões de Substituição
                <v-spacer />
                <v-btn icon @click="cancel"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-divider />

            <v-card-text class="pt-4" style="max-height: 70vh">
                <v-card outlined class="mb-3 pa-3">
                    <div class="caption grey--text">Sala atual</div>
                    <div class="subtitle-1 font-weight-medium">
                        {{ sourceLabel }}
                    </div>
                    <div class="body-2 grey--text" v-if="result.source">
                        <div v-if="sourceClassLabel" class="font-weight-medium grey--text text--darken-2">
                            <v-icon x-small class="mr-1">mdi-school-outline</v-icon>
                            Turma: {{ sourceClassLabel }}
                        </div>
                        <div>
                            {{ result.source.headcount }} alunos
                            <span v-if="result.source.day_name"> · {{ result.source.day_name }}</span>
                            · {{ formatTime(result.source.hour_start) }} – {{ formatTime(result.source.hour_end) }}
                        </div>
                        <div v-if="result.source.headcount === 0" class="orange--text text--darken-2 text-caption mt-1">
                            <v-icon x-small color="orange darken-2">mdi-alert-outline</v-icon>
                            Esta turma não tem alunos lançados — substituí-la não reduz a ocupação da sala.
                        </div>
                    </div>
                </v-card>

                <v-alert
                    v-if="isConsolidatedBlock"
                    type="info"
                    dense
                    text
                    class="mb-3 block-warning"
                    icon="mdi-link-variant"
                >
                    <strong>Esta alteração afetará o bloco completo de
                        {{ blockDurationLabel }} minutos desta disciplina</strong>
                    <span class="d-block text-caption">
                        {{ classScheduleIds.length }} tempos de aula sequenciais
                        serão movidos juntos para a nova sala. Operação
                        atômica — todos os tempos mudam ou nenhum muda.
                    </span>
                </v-alert>

                <emc-loader-progress :show="loading" />

                <v-alert v-if="loadError" type="error" dense text>
                    Não foi possível carregar as sugestões. Tente novamente.
                </v-alert>

                <div v-else-if="!loading && noResults">
                    <v-alert type="warning" dense text class="mb-3">
                        <strong>Sem opções de substituição.</strong>
                        {{ messages || 'Nenhuma sala disponível atende ao perfil desta turma.' }}
                    </v-alert>

                    <v-card outlined class="pa-3 mb-3 reconsider-card">
                        <div class="d-flex align-start">
                            <v-icon color="indigo darken-2" class="mr-2 mt-1">mdi-lightbulb-on-outline</v-icon>
                            <div class="flex-grow-1">
                                <div class="subtitle-2 font-weight-medium mb-1">
                                    Repensar a oferta
                                </div>
                                <div class="text-caption grey--text text--darken-1 mb-2">
                                    Sem sala compatível, registre uma solicitação
                                    para revisar a oferta desta turma (ex.: dividir
                                    em duas, mudar horário, reduzir matrículas).
                                    Sua solicitação é auditada com usuário, data,
                                    hora e IP.
                                </div>
                                <v-textarea
                                    v-model="reconsiderNote"
                                    label="Justificativa (opcional)"
                                    rows="2"
                                    outlined dense
                                    counter="1000"
                                    :rules="[v => !v || v.length <= 1000 || 'Máximo 1000 caracteres.']"
                                    class="mb-2"
                                />
                                <v-btn
                                    small
                                    color="indigo darken-2"
                                    dark
                                    :loading="reconsidering"
                                    :disabled="reconsidering || reconsiderDone"
                                    @click="requestReconsider"
                                >
                                    <v-icon left small>{{ reconsiderDone ? 'mdi-check' : 'mdi-send-outline' }}</v-icon>
                                    {{ reconsiderDone ? 'Solicitação registrada' : 'Solicitar revisão da oferta' }}
                                </v-btn>
                            </div>
                        </div>
                    </v-card>
                </div>

                <div v-else>
                    <div
                        v-for="suggestion in result.suggestions"
                        :key="suggestion.ambience_id"
                        class="suggestion-card mb-2"
                        :class="{ 'recommended-card': suggestion.recommended_rank === 1 }"
                    >
                        <div class="d-flex align-center">
                            <div class="flex-grow-1">
                                <div class="d-flex align-center">
                                    <v-icon small class="mr-1">mdi-door</v-icon>
                                    <span class="subtitle-2 font-weight-medium">{{ suggestion.name }}</span>
                                    <v-chip
                                        v-if="suggestion.recommended_rank === 1"
                                        x-small color="success" dark class="ml-2"
                                    >
                                        Recomendada
                                    </v-chip>
                                </div>
                                <div class="text-caption grey--text mt-1">
                                    Capacidade ativa: {{ suggestion.active_capacity }}
                                    · Vagas livres após troca: {{ suggestion.spare_seats }}
                                    <span v-if="suggestion.same_block"> · Mesmo bloco</span>
                                    <span v-if="suggestion.same_floor"> · Mesmo andar</span>
                                </div>
                            </div>
                            <v-btn
                                small
                                color="primary"
                                :loading="applying"
                                :disabled="applying"
                                @click="select(suggestion)"
                            >
                                Substituir
                            </v-btn>
                        </div>
                    </div>
                </div>

                <v-divider class="my-3" />

                <div class="d-flex grey--text text-caption flex-wrap">
                    <div class="mr-4">
                        Encontradas: <strong>{{ result.suggestions.length }}</strong>
                    </div>
                    <div class="mr-4" v-if="result.match_status">
                        Status: <strong>{{ statusLabel }}</strong>
                    </div>
                </div>
            </v-card-text>

            <v-divider />
            <v-card-actions>
                <v-spacer />
                <v-btn text @click="cancel">Fechar</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'SysAmbienceSuggestSwapModal',
    props: {
        show: { type: Boolean, default: false },
        sourceAmbience: { type: Object, default: null },
        sourceOccupation: { type: Object, default: null },
        classScheduleId: { type: [Number, String], default: null },
        classScheduleIds: { type: Array, default: () => [] },
        blockDurationMinutes: { type: Number, default: 0 },
        compositeKey: { type: Object, default: null },
        filters: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            showModal: this.show,
            loading: false,
            loadError: false,
            applying: false,
            feedback: { show: false, color: 'success', message: '' },
            result: { source: null, suggestions: [], match_status: null, messages: [] },
            reconsidering: false,
            reconsiderDone: false,
            reconsiderNote: '',
        }
    },
    computed: {
        sourceLabel() {
            if (this.result.source) {
                return this.result.source.ambience_name || ('Ambiente #' + this.result.source.ambience_id)
            }
            return this.sourceAmbience ? (this.sourceAmbience.name || 'Sala atual') : '-'
        },
        sourceClassLabel() {
            const s = this.result.source
            if (!s) return ''
            const code = s.discipline_code || s.discipline_name
            if (!code) return ''
            return s.group_code ? `${code}-${s.group_code}` : code
        },
        noResults() {
            return !this.result.suggestions || !this.result.suggestions.length
        },
        messages() {
            return Array.isArray(this.result.messages) ? this.result.messages.join(' ') : ''
        },
        statusLabel() {
            return ({
                ok: 'OK',
                no_slot: 'Slot não encontrado',
                no_capacity: 'Sem capacidade',
                no_candidates: 'Sem candidatas',
            })[this.result.match_status] || this.result.match_status
        },
        isConsolidatedBlock() {
            return Array.isArray(this.classScheduleIds) && this.classScheduleIds.length > 1
        },
        blockDurationLabel() {
            const fromProp = Number(this.blockDurationMinutes || 0)
            if (fromProp > 0) return fromProp
            return (this.classScheduleIds?.length || 0) * 50
        },
    },
    watch: {
        show(value) {
            this.showModal = value
            if (value) {
                this.fetch()
            }
        },
    },
    methods: {
        onShowChange(value) {
            this.showModal = value
            this.$emit('update:show', value)
        },
        cancel() {
            this.onShowChange(false)
        },
        async fetch() {
            this.loadError = false
            this.result = { source: null, suggestions: [], match_status: null, messages: [] }
            this.reconsiderDone = false
            this.reconsiderNote = ''

            const payload = this.buildPayload()
            if (!payload) {
                this.result.match_status = 'no_slot'
                this.result.messages = ['Não foi possível identificar o slot de aula desta sala.']
                return
            }

            this.loading = true
            try {
                const response = await this.$http.post('substitution_suggestions', payload)
                const body = response.body || {}
                this.result = body.data ?? body ?? this.result
            } catch (error) {
                this.loadError = true
            } finally {
                this.loading = false
            }
        },
        buildPayload() {
            if (Array.isArray(this.classScheduleIds) && this.classScheduleIds.length > 0) {
                return { class_schedule_id: Number(this.classScheduleIds[0]) }
            }
            if (this.classScheduleId) {
                return { class_schedule_id: Number(this.classScheduleId) }
            }
            if (this.compositeKey?.grid_id && this.compositeKey?.day_of_week_id && this.compositeKey?.hour_id) {
                return {
                    grid_id: this.compositeKey.grid_id,
                    day_of_week_id: this.compositeKey.day_of_week_id,
                    hour_id: this.compositeKey.hour_id,
                    join_id: this.compositeKey.join_id || null,
                }
            }
            // Fallback (ex.: chamada do detalhe da sala sem slot selecionado):
            // backend resolve o primeiro slot ativo daquele ambiente.
            if (this.sourceAmbience?.id) {
                return { source_ambience_id: Number(this.sourceAmbience.id) }
            }
            return null
        },
        showFeedback(color, message) {
            this.feedback = { show: true, color, message }
        },
        async select(suggestion) {
            const blockIds = Array.isArray(this.classScheduleIds) && this.classScheduleIds.length > 1
                ? this.classScheduleIds.map(Number)
                : null
            const csid = blockIds
                ? blockIds[0]
                : (this.result?.source?.class_schedule_id || Number(this.classScheduleId))
            if (!csid && !blockIds) {
                this.showFeedback('error', 'Não foi possível identificar o slot da aula.')
                return
            }
            this.applying = true
            try {
                if (blockIds) {
                    await this.$http.put(
                        'class_schedules/consolidated/ambience',
                        {
                            class_schedule_ids: blockIds,
                            target_ambience_id: Number(suggestion.ambience_id),
                        }
                    )
                } else {
                    await this.$http.put(
                        `class_schedules/${csid}/ambience/apply`,
                        { target_ambience_id: Number(suggestion.ambience_id) }
                    )
                }
                this.showFeedback('success', `Sala alterada para ${suggestion.name}.`)
                this.$emit('applied', {
                    source: this.result.source,
                    target: suggestion,
                    affected_count: blockIds ? blockIds.length : 1,
                })
                // Fecha o modal só depois do snackbar ser visto.
                setTimeout(() => this.cancel(), 1200)
            } catch (error) {
                const body = error?.body || error?.response?.data || {}
                const msg = body?.errors?.target_ambience_id?.[0]
                    || (typeof body?.message === 'string' ? body.message : null)
                    || body?.message?.error
                    || 'Falha ao aplicar substituição.'
                this.showFeedback('error', msg)
            } finally {
                this.applying = false
            }
        },
        formatTime(value) {
            if (!value) return '--:--'
            return String(value).slice(0, 5)
        },

        /**
         * "Repensar a oferta": registra audit event quando o motor não
         * encontra sala compatível. Backend captura IP/UA via request.
         * Resolve o ambience_id a partir de sourceAmbience (caminho do
         * detalhe da sala) ou do result.source (caminho do slot).
         */
        async requestReconsider() {
            const ambienceId = Number(
                this.sourceAmbience?.id || this.result?.source?.ambience_id || 0
            )
            if (!ambienceId) {
                this.showFeedback('error', 'Não foi possível identificar a sala.')
                return
            }
            this.reconsidering = true
            try {
                await this.$http.post('governance/offer-reconsider', {
                    ambience_id: ambienceId,
                    note: this.reconsiderNote || null,
                })
                this.reconsiderDone = true
                this.showFeedback('success', 'Solicitação de revisão registrada.')
            } catch (error) {
                const body = error?.body || error?.response?.data || {}
                const msg = (typeof body?.message === 'string' ? body.message : null)
                    || 'Falha ao registrar solicitação.'
                this.showFeedback('error', msg)
            } finally {
                this.reconsidering = false
            }
        },
    },
}
</script>

<style scoped>
.suggestion-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px;
    transition: border-color .15s;
}
.suggestion-card:hover {
    border-color: #6366f1;
}
.recommended-card {
    border-color: #10b981;
    background: #f1fbf7;
}
.reconsider-card {
    border: 1px dashed #c7d2fe !important;
    background: #f5f3ff;
}
.block-warning {
    border-left: 3px solid #2563eb !important;
}
</style>
